import { acceptHMRUpdate, defineStore } from 'pinia'
import { isAxiosError } from 'axios'
import type { User } from '@/types/User.ts'
import { useCookies } from '@vueuse/integrations/useCookies'
import { TOKEN_NAME as AUTH_TOKEN_NAME, authType, type AuthTypes } from '@/enum/auth.ts'
import type { AuthResponse, LoginForm, RegisterForm } from '@/types/forms/Auth.ts'
import { updateToken } from '@/utils/auth.ts'
import { cacheKeys } from '@/enum/cacheKeys.ts'
import { useWaitingRequest } from '@/composables/useWaitingRequest.ts'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>()
  const loading = ref(false)
  const cookies = useCookies([AUTH_TOKEN_NAME])
  const chatStore = useChatStore()
  const trackStore = useTrackStore()
  const cacheStore = useCacheStore()
  const subscriptionStore = useSubscriptionsStore()
  const isPersonalizationFieldsCompleted = computed<boolean>(() => {
    return Boolean(user.value && user.value.gender && user.value.birthdate)
  })

  // computed
  const isAuthenticated = computed(() => !!user.value)

  const isSubscribed = computed(() => {
    if (!user.value) {
      return false
    }

    return user.value.subscription !== null && user.value.has_valid_subscription
  })

  const isPhoneVerified = computed(() => {
    if (!user.value) {
      return false
    }

    return !!user.value.phone_verified_at
  })

  const getToken = () => cookies.get(AUTH_TOKEN_NAME)

  const reAuthenticate = () => {
    const waitingRequest = useWaitingRequest<User>()

    return async () => {
      const token = cookies.get(AUTH_TOKEN_NAME)

      if (!token) {
        return
      }

      try {
        loading.value = true

        const result = await waitingRequest.execute(async () => {
          const response = await useForm<Record<string, unknown>, User>('get', '/auth', {}).submit()
          return response.data
        })

        user.value = result
        trackStore.onLogin(result)

        return result
      } catch (e) {
        if (isAxiosError(e) && [401, 419].includes(e.response?.status ?? 0)) {
          cookies.remove(AUTH_TOKEN_NAME)
        }

        throw e
      } finally {
        loading.value = false
      }
    }
  }

  const authenticate = async (auth: User, token: string) => {
    user.value = auth

    cookies.set(AUTH_TOKEN_NAME, token, {
      path: '/',
      expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
    })

    cacheStore.remove(cacheKeys.messageLimitAuthDialog)

    updateToken(token)
  }

  const logout = async () => {
    try {
      loading.value = true

      await useForm('delete', '/auth', {}).submit()
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false

      user.value = null

      cookies.remove(AUTH_TOKEN_NAME, {
        path: '/',
      })
    }
  }

  const profileCompletion = computed(() => {
    let percentage = 0
    const fields: (keyof User)[] = [
      'first_name',
      'last_name',
      'gender',
      'birthdate',
      'phone',
      'email',
    ]

    for (const field of fields) {
      if (user.value?.[field]) {
        percentage += 100 / fields.length
      }
    }

    return Math.min(percentage, 100)
  })

  const getAuthForm = (type: AuthTypes = authType.LOG_IN, data: Record<string, unknown> = {}) => {
    if (type === authType.LOG_IN) {
      return useForm<LoginForm, AuthResponse>('post', '/auth/login', {
        identity: '',
        password: '',
        ...data,
      })
    }

    return useForm<RegisterForm, AuthResponse>('post', '/auth', {
      password: '',
      password_confirmation: '',
      ...data,
    })
  }

  const getUpdateProfileForm = () => {
    return useForm<Partial<User>, User>('put', 'users/profile', user.value ?? {
      gender: undefined,
      birthdate: undefined,
    })
  }

  const getPersonalizationForm = () => {
    return useForm<Partial<User>, User>('put', 'users/profile', {
      gender: user.value?.gender || undefined,
      birthdate: user.value?.birthdate || undefined,
    })
  }


  const managePersonalizationDisabling = () => {
    const v = cacheStore.get(cacheKeys.disablePersonalization)

    if (!v) return

    if (typeof v.value !== 'number') v.value = 2

    if (v.value == 0) {
      cacheStore.remove(cacheKeys.disablePersonalization)
    } else {
      cacheStore.set(cacheKeys.disablePersonalization, v.value - 1)
    }
  }

  watch(user, val => {
    if (!val) {
      subscriptionStore.subscription = null
      return
    }

    if (val.subscription) {
      subscriptionStore.subscription = val.subscription
    }
  })

  watch(isAuthenticated, (value, oldValue) => {
    if (value && !oldValue) {
      chatStore.$reset()
      chatStore.fetchChats()
    }

    if (!value && oldValue) {
      chatStore.$reset()
      chatStore.chats = []
    }
  })

  return {
    // state
    user,
    loading,

    // actions
    logout,
    authenticate,
    getAuthForm,
    reAuthenticate: reAuthenticate(),
    getUpdateProfileForm,
    getPersonalizationForm,
    managePersonalizationDisabling,
    getToken,
    // computed
    isAuthenticated,
    isSubscribed,
    isPersonalizationFieldsCompleted,
    profileCompletion,
    isPhoneVerified,
  }
})

// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
